import { useEffect, useState } from 'react';

/**
 * Returns `true` on macOS, `false` everywhere else (Windows, Linux, web).
 *
 * Uses the Tauri `os` plugin under the hood, so it returns `false` on the
 * web build (where the plugin is unavailable) — which is the correct
 * default for macOS-only UI surfaces.
 *
 * The result is `null` until the first async detection resolves; callers
 * that want to render conditionally should treat `null` the same as
 * `false` to avoid a flash of macOS-only UI on non-macOS clients.
 */
export function useIsMacOS(): boolean | null {
  const [isMacOS, setIsMacOS] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { platform } = await import('@tauri-apps/plugin-os');
        const p = await platform();
        if (!cancelled) setIsMacOS(p === 'macos');
      } catch {
        if (!cancelled) setIsMacOS(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return isMacOS;
}
